import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { Link, Navigate, useParams } from "react-router-dom";
import { PageHeader } from "../components/AppShell.js";
import { Avatar } from "../components/Avatar.js";
import { FranchiseBadge } from "../components/FranchiseBadge.js";
import { Button, Notice } from "../components/ui.js";
import {
  auctionLotLabel,
  canCloseLot,
  canFinish,
  clearBid,
  currentLot,
  fetchPoolState,
  finishPool,
  isPaused,
  markUnsold,
  nextLot,
  nextPlayer,
  openPool,
  pausePool,
  playerFacts,
  raiseBid,
  sell,
  stillToOffer,
  undoLast,
} from "../lib/auction.js";
import { fetchAuctionRules, rulesFor } from "../lib/auction-rules.js";
import { hasRole, useAuth } from "../lib/auth.js";
import type { AuctionCurrency } from "../lib/currency.js";
import { fetchFranchises, type FranchiseSetup } from "../lib/franchises.js";
import { fetchTournaments } from "../lib/tournaments.js";
import type { AuctionPoolState, Tournament, TournamentEvent } from "../lib/types.js";
import {
  auctionStateKey,
  FranchiseHeading,
  LotStrip,
  PlaceList,
  TeamCards,
  usePeople,
  useRefresh,
  type People,
} from "./auction/parts.js";

/** How often the screen asks again while the pool is live. A second organiser's taps show up. */
const CONSOLE_REFRESH_MS = 3000;

/**
 * One pool of the auction, on a screen of its own: open it, call it player by player, or place
 * its players by hand, then finish it and adjust the teams. Reached from the auction hub.
 * Organisers only; the database refuses everybody else.
 */
export function TournamentAuctionPool() {
  const { tournamentId, eventId } = useParams<{
    tournamentId: string;
    eventId: string;
  }>();
  const { member } = useAuth();
  const { data: tournaments, isPending } = useQuery({
    queryKey: ["tournaments"],
    queryFn: fetchTournaments,
  });
  const tournament = tournaments?.find((t) => t.id === tournamentId);
  const event = tournament?.events.find((e) => e.id === eventId);

  if (!hasRole(member, "organiser")) {
    return <Navigate to={`/tournaments/${tournamentId}`} replace />;
  }
  if (isPending) {
    return (
      <main className="mx-auto max-w-5xl px-4 pb-16 pt-6">
        <p className="micro">Loading…</p>
      </main>
    );
  }
  if (!tournament || !event || !tournament.auction) {
    return <Navigate to={`/tournaments/${tournamentId}/auction`} replace />;
  }
  if (tournament.auctionStartedAt === null) {
    return <Navigate to={`/tournaments/${tournament.id}/auction`} replace />;
  }

  return <PoolScreen tournament={tournament} event={event} />;
}

function PoolScreen({ tournament, event }: { tournament: Tournament; event: TournamentEvent }) {
  const { data: setup } = useQuery({
    queryKey: ["franchises", tournament.id],
    queryFn: () => fetchFranchises(tournament.id),
  });
  const { data: rules } = useQuery({
    queryKey: ["auction-rules", tournament.id],
    queryFn: () => fetchAuctionRules(tournament.id),
  });
  // Every pool's state, so this one knows whether another is holding the floor.
  const states = useQueries({
    queries: tournament.events.map((e) => ({
      queryKey: auctionStateKey(e.id),
      queryFn: () => fetchPoolState(e.id),
      refetchInterval: e.auctionFinishedAt === null ? CONSOLE_REFRESH_MS : (false as const),
    })),
  });
  const index = tournament.events.findIndex((e) => e.id === event.id);
  const state = states[index]?.data;
  const openElsewhere = tournament.events.find(
    (e, i) => e.id !== event.id && states[i]?.data?.open,
  );

  const status = !state
    ? ""
    : state.finished
      ? "Finished"
      : state.open
        ? "Open"
        : isPaused(state)
          ? `Paused · ${state.players.filter((p) => p.squadId !== null).length} of ${state.players.length} placed`
          : state.fill === "assigned"
            ? "Placing"
            : "Waiting";

  return (
    <main className="mx-auto max-w-5xl px-4 pb-16 pt-6">
      <Link
        to={`/tournaments/${tournament.id}/auction`}
        className="micro mb-4 inline-block underline"
      >
        ← All pools
      </Link>
      <PageHeader eyebrow={`Auction · ${tournament.name}`} title={event.name} />
      <p className="micro -mt-2 mb-5">
        {event.squadFill === "assigned" ? "Placed by you" : "Auction"} · {status}
        {state?.open ? (
          <>
            {" · "}
            <Link to={`/tournaments/${tournament.id}/auction/board`} className="underline">
              Board
            </Link>
          </>
        ) : null}
      </p>
      {!state || !setup || !rules ? (
        <p className="micro">Loading…</p>
      ) : state.finished ? (
        <Finished event={event} state={state} setup={setup} currency={tournament.auctionCurrency} />
      ) : state.fill === "assigned" ? (
        <AssignedPool
          event={event}
          state={state}
          setup={setup}
          currency={tournament.auctionCurrency}
        />
      ) : state.open ? (
        <Console
          event={event}
          state={state}
          setup={setup}
          currency={tournament.auctionCurrency}
          organiserPicks={rulesFor(event.id, rules).nextUp === "organiser"}
        />
      ) : (
        <OpenPool
          event={event}
          state={state}
          setup={setup}
          currency={tournament.auctionCurrency}
          blockedBy={openElsewhere ?? null}
        />
      )}
    </main>
  );
}

function OpenPool({
  event,
  state,
  setup,
  currency,
  blockedBy,
}: {
  event: TournamentEvent;
  state: AuctionPoolState;
  setup: FranchiseSetup;
  currency: AuctionCurrency;
  blockedBy: TournamentEvent | null;
}) {
  const refresh = useRefresh(event);
  const people = usePeople(event, setup, currency);
  const open = useMutation({
    mutationFn: () => openPool(event.id),
    onSuccess: refresh,
  });
  return (
    <>
      <LotStrip state={state} people={people} />
      {blockedBy ? (
        <p className="mb-3 text-sm text-ink-muted">
          {blockedBy.name} is open. Pause or finish it to open this one.
        </p>
      ) : null}
      {open.error ? <Notice tone="attention">{open.error.message}</Notice> : null}
      <div className="mb-8">
        <Button
          tone="primary"
          disabled={blockedBy !== null || open.isPending}
          onClick={() => open.mutate()}
        >
          {isPaused(state) ? "Resume" : "Open"} {event.name}
        </Button>
      </div>
      <TeamCards event={event} state={state} people={people} />
    </>
  );
}

/** The room's console for the open pool. */
function Console({
  event,
  state,
  setup,
  currency,
  organiserPicks,
}: {
  event: TournamentEvent;
  state: AuctionPoolState;
  setup: FranchiseSetup;
  currency: AuctionCurrency;
  organiserPicks: boolean;
}) {
  const refresh = useRefresh(event);
  const people = usePeople(event, setup, currency);
  const lot = currentLot(state);

  const next = useMutation({
    mutationFn: (memberId: string | null) => nextPlayer(event.id, memberId),
    onSuccess: refresh,
  });
  // The raise names the player and bid on screen, so a tap made against a bid a second organiser
  // (or a captain) has just moved is refused instead of landing a step higher.
  const raise = useMutation({
    mutationFn: (squadId: string) =>
      raiseBid(event.id, squadId, {
        memberId: state.up!.memberId,
        bid: state.up!.bid,
      }),
    onSettled: refresh,
  });
  const sold = useMutation({
    mutationFn: () => sell(event.id),
    onSuccess: refresh,
  });
  const unsold = useMutation({
    mutationFn: () => markUnsold(event.id),
    onSuccess: refresh,
  });
  const clear = useMutation({
    mutationFn: () => clearBid(event.id),
    onSuccess: refresh,
  });
  const undo = useMutation({
    mutationFn: () => undoLast(event.id),
    onSuccess: refresh,
  });
  const finish = useMutation({
    mutationFn: () => finishPool(event.id),
    onSuccess: refresh,
  });
  const close = useMutation({
    mutationFn: () => nextLot(event.id),
    onSuccess: refresh,
  });
  const pause = useMutation({
    mutationFn: () => pausePool(event.id),
    onSuccess: refresh,
  });

  const error =
    pause.error ??
    close.error ??
    next.error ??
    raise.error ??
    sold.error ??
    unsold.error ??
    clear.error ??
    undo.error ??
    finish.error;
  const busy = [next, raise, sold, unsold, clear, undo, finish, close, pause].some(
    (m) => m.isPending,
  );
  const waiting = stillToOffer(state);
  const inLot = waiting.filter((p) => !lot || p.lotId === lot.lotId);
  // Who may come up next: those of the lot offered the fewest times, as the database allows.
  const fewest = Math.min(...inLot.map((p) => p.offers));
  const eligible = inLot.filter((p) => p.offers === fewest);
  const squadName = (squadId: string | null) => {
    const squad = state.squads.find((s) => s.squadId === squadId);
    return squad ? people.franchiseName(squad.franchiseId) : "nobody";
  };

  return (
    <>
      <LotStrip state={state} people={people} />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_260px]">
        <div>
          {state.up ? (
            <Stage
              state={state}
              people={people}
              busy={busy}
              onRaise={(squadId) => raise.mutate(squadId)}
              onSold={() => sold.mutate()}
              onUnsold={() => unsold.mutate()}
              onClear={() => clear.mutate()}
            />
          ) : (
            <div className="mb-4 border-2 p-5" style={{ borderColor: "var(--color-line)" }}>
              {waiting.length > 0 ? (
                organiserPicks ? (
                  <>
                    <p className="micro mb-3">
                      Who comes up next?{fewest > 0 ? " · offered again" : ""}
                    </p>
                    <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {eligible.map((p) => {
                        const who = people.person(p.memberId);
                        return (
                          <li key={p.memberId}>
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => next.mutate(p.memberId)}
                              aria-label={`Put up ${who.name}`}
                              className="flex w-full items-center gap-2 border-2 p-2 text-left text-sm font-semibold"
                              style={{ borderColor: "var(--color-line-muted)" }}
                            >
                              <Avatar name={who.name} url={who.avatarUrl} size={36} />
                              <span className="min-w-0 truncate">{who.name}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                ) : (
                  <div className="flex flex-wrap items-center gap-4">
                    <Button tone="primary" disabled={busy} onClick={() => next.mutate(null)}>
                      Next player
                    </Button>
                  </div>
                )
              ) : canFinish(state) ? (
                <p className="text-sm">Everybody has a team. Finish the pool to publish them.</p>
              ) : (
                <p className="text-sm">Nobody is left to offer. Place the rest below.</p>
              )}
              {lot && waiting.length > 0 ? (
                <div
                  className="mt-4 flex flex-wrap items-center gap-4 border-t pt-4"
                  style={{ borderColor: "var(--color-line-muted)" }}
                >
                  <span className="text-sm text-ink-muted" aria-label="Current lot">
                    {auctionLotLabel(lot)} · {lot.toOffer} to offer · {lot.toOfferAgain} to offer
                    again
                  </span>
                  {canCloseLot(state) ? (
                    <Button tone="line" disabled={busy} onClick={() => close.mutate()}>
                      Next lot
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>
          )}

          {error ? <Notice tone="attention">{error.message}</Notice> : null}

          <div className="mb-6 flex flex-wrap gap-2">
            {state.lastEntry && !state.up ? (
              <Button tone="quiet" disabled={busy} onClick={() => undo.mutate()}>
                Undo: {entryLabel(state, people, squadName)}
              </Button>
            ) : null}
            {canFinish(state) ? (
              <Button tone="primary" disabled={busy} onClick={() => finish.mutate()}>
                Finish {event.name}
              </Button>
            ) : null}
            {!state.up ? (
              <Button tone="line" disabled={busy} onClick={() => pause.mutate()}>
                Pause {event.name}
              </Button>
            ) : null}
          </div>

          <PlaceList event={event} state={state} people={people} />
        </div>

        <aside aria-label="Still to come">
          <p className="micro mb-2">Still to come · {waiting.length}</p>
          <ul className="flex flex-col gap-1.5">
            {waiting.slice(0, 16).map((p) => {
              const who = people.person(p.memberId);
              const l = state.lots.find((x) => x.lotId === p.lotId);
              return (
                <li key={p.memberId} className="flex items-center gap-2 text-sm">
                  <Avatar name={who.name} url={who.avatarUrl} size={28} />
                  <span className="min-w-0 flex-1 truncate">{who.name}</span>
                  {p.state === "reoffer" ? (
                    <span className="font-mono text-[10px] uppercase text-ink-muted">again</span>
                  ) : null}
                  {l ? (
                    <span className="font-mono text-[10px] text-ink-muted">
                      {auctionLotLabel(l)}
                    </span>
                  ) : null}
                </li>
              );
            })}
            {waiting.length > 16 ? (
              <li className="font-mono text-[11px] text-ink-muted">
                and {waiting.length - 16} more
              </li>
            ) : null}
          </ul>
        </aside>
      </div>

      <h2 className="micro mb-3 mt-8">Teams</h2>
      <TeamCards event={event} state={state} people={people} amendable />
    </>
  );
}

/** What Undo would take back: a sale, an unsold, a placement or a lot close. */
function entryLabel(
  state: AuctionPoolState,
  people: People,
  squadName: (squadId: string | null) => string,
): string {
  const entry = state.lastEntry;
  if (!entry) return "";
  if (entry.kind === "lot_closed") {
    const lot = state.lots.find((l) => l.lotId === entry.lotId);
    return `${lot ? auctionLotLabel(lot) : "the lot"} closed`;
  }
  const name = people.person(entry.memberId).name;
  if (entry.kind === "unsold") return `${name} unsold`;
  const to = squadName(entry.squadId);
  if (!to) return name;
  return `${name} to ${to}${entry.price === null ? "" : ` for ${people.price(entry.price)}`}`;
}

/** The player up, the bid, and a tile per franchise to record who raised. */
function Stage({
  state,
  people,
  busy,
  onRaise,
  onSold,
  onUnsold,
  onClear,
}: {
  state: AuctionPoolState;
  people: People;
  busy: boolean;
  onRaise: (squadId: string) => void;
  onSold: () => void;
  onUnsold: () => void;
  onClear: () => void;
}) {
  const up = state.up!;
  const who = people.person(up.memberId);
  const lot = state.lots.find((l) => l.lotId === up.lotId);
  const holder = state.squads.find((s) => s.squadId === up.holderId);
  const holderFranchise = holder ? people.franchise(holder.franchiseId) : undefined;

  return (
    <section
      className="mb-4 border-2"
      style={{ borderColor: "var(--color-line)" }}
      aria-label="Up now"
    >
      <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:gap-5">
        <div className="flex min-w-0 items-center gap-4 sm:flex-1">
          <Avatar name={who.name} url={who.avatarUrl} size={96} />
          <div className="min-w-0">
            <p className="micro">
              Up now
              {up.offers > 0 ? " · offered again" : ""}
              {lot ? ` · ${auctionLotLabel(lot)} · base ${people.price(lot.basePrice ?? 0)}` : ""}
            </p>
            <p className="font-display break-words text-4xl leading-[0.95] sm:text-6xl">
              {who.name}
            </p>
            {playerFacts(up.details).length > 0 ? (
              <p
                className="mt-2 font-mono text-[12px] text-ink-muted"
                aria-label="About the player"
              >
                {playerFacts(up.details).join(" · ")}
              </p>
            ) : null}
          </div>
        </div>
        <div
          className="flex items-end justify-between gap-3 sm:block sm:text-right"
          aria-live="polite"
        >
          <div>
            <p className="micro">{up.bid === null ? "Opening bid" : "Current bid"}</p>
            <p className="font-display text-6xl leading-none" aria-label="Current bid">
              <span className={up.bid === null ? "" : "highlight px-2"}>
                {people.price(up.bid ?? up.nextBid)}
              </span>
            </p>
          </div>
          {up.forced && up.bid === null ? (
            <p className="micro mt-1" aria-label="Forced player">
              Only {up.forced.name} can take this player
            </p>
          ) : null}
          <p
            className="mt-1 flex items-center justify-end gap-2 text-sm font-semibold"
            aria-label="Held by"
          >
            {holder ? (
              <>
                {holderFranchise ? <FranchiseBadge franchise={holderFranchise} size={22} /> : null}
                {people.franchiseName(holder.franchiseId)}
              </>
            ) : (
              <span className="text-ink-muted">No bid yet</span>
            )}
          </p>
        </div>
      </div>

      <div
        className="grid grid-cols-2 gap-px border-t-2 sm:grid-cols-4"
        style={{
          borderColor: "var(--color-line)",
          background: "var(--color-line-muted)",
        }}
      >
        {state.squads.map((squad) => {
          const franchise = people.franchise(squad.franchiseId);
          const name = people.franchiseName(squad.franchiseId);
          const holding = up.holderId === squad.squadId;
          const blocked = squad.refusal !== null && !holding;
          return (
            <button
              key={squad.squadId}
              type="button"
              disabled={busy || squad.refusal !== null}
              onClick={() => onRaise(squad.squadId)}
              aria-label={`${name} raise to ${people.price(up.nextBid)}`}
              title={blocked ? squad.refusal! : undefined}
              className={`flex min-h-[112px] flex-col gap-2 p-3 text-left ${holding ? "highlight" : ""}`}
              style={{
                background: holding ? undefined : "var(--color-ground)",
                borderTop: `4px solid ${franchise?.colour ?? "var(--color-line)"}`,
                opacity: blocked ? 0.45 : 1,
              }}
            >
              <FranchiseHeading franchise={franchise} name={name} size={32} wrap />
              <span className="font-mono text-[12px]">{people.price(squad.left)} left</span>
              <span className="mt-auto text-[12px] font-semibold">
                {holding
                  ? "Holds the bid"
                  : blocked
                    ? squad.refusal
                    : `Raise to ${people.price(up.nextBid)}`}
              </span>
            </button>
          );
        })}
      </div>

      <div
        className="flex flex-wrap gap-2 border-t-2 p-4"
        style={{ borderColor: "var(--color-line)" }}
      >
        <Button tone="primary" disabled={busy || up.holderId === null} onClick={onSold}>
          {holder
            ? `Sold to ${people.franchiseName(holder.franchiseId)} for ${people.price(up.bid!)}`
            : "Sold"}
        </Button>
        <Button tone="line" disabled={busy || up.holderId !== null} onClick={onUnsold}>
          Unsold
        </Button>
        <Button tone="quiet" disabled={busy || up.bid === null} onClick={onClear}>
          Clear bid
        </Button>
      </div>
    </section>
  );
}

/** A pool placed by the organiser: the players still to place, the teams so far, Finish. */
function AssignedPool({
  event,
  state,
  setup,
  currency,
}: {
  event: TournamentEvent;
  state: AuctionPoolState;
  setup: FranchiseSetup;
  currency: AuctionCurrency;
}) {
  const refresh = useRefresh(event);
  const people = usePeople(event, setup, currency);
  const finish = useMutation({
    mutationFn: () => finishPool(event.id),
    onSuccess: refresh,
  });
  const undo = useMutation({
    mutationFn: () => undoLast(event.id),
    onSuccess: refresh,
  });
  const error = finish.error ?? undo.error;

  return (
    <>
      <PlaceList event={event} state={state} people={people} />
      {error ? <Notice tone="attention">{error.message}</Notice> : null}
      <div className="mb-6 flex flex-wrap gap-2">
        {state.lastEntry ? (
          <Button tone="quiet" disabled={undo.isPending} onClick={() => undo.mutate()}>
            Undo: {entryLabel(state, people, () => "")}
          </Button>
        ) : null}
        {canFinish(state) ? (
          <Button tone="primary" disabled={finish.isPending} onClick={() => finish.mutate()}>
            Finish {event.name}
          </Button>
        ) : null}
      </div>
      <h2 className="micro mb-3">Teams</h2>
      <TeamCards event={event} state={state} people={people} />
    </>
  );
}

/** A finished pool: the teams, each player with a picker to move them. */
function Finished({
  event,
  state,
  setup,
  currency,
}: {
  event: TournamentEvent;
  state: AuctionPoolState;
  setup: FranchiseSetup;
  currency: AuctionCurrency;
}) {
  const people = usePeople(event, setup, currency);
  return (
    <>
      <p className="mb-5 text-sm text-ink-muted">
        The teams are public. You can still move a player; what was paid stays with the team that
        paid it.
      </p>
      <TeamCards event={event} state={state} people={people} editable />
    </>
  );
}
